const cities = [
  { name: "Windermere", zips: "34786" },
  { name: "Orlando", zips: "32801–32899" },
  { name: "Dr. Phillips", zips: "32819, 32836" },
  { name: "Ocoee", zips: "34761" },
  { name: "Winter Garden", zips: "34787" },
  { name: "Clermont", zips: "34711" },
  { name: "Celebration", zips: "34747" },
  { name: "Kissimmee", zips: "34741–34758" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your Windermere lakefront home or Orlando office in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function WindermereLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] pt-12 md:pt-14 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Windermere &amp; Southwest Orange County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Windermere, Dr. Phillips, Ocoee, Winter Garden, or anywhere in southwest Orange County, Fast Passport Center makes expedited passport processing simple — no drive downtown, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Windermere occupies a singular position in the Orlando metropolitan area. Where most Orlando suburbs grew around retail corridors, theme park employment, or college expansion, Windermere was built from the beginning around an irreplaceable natural asset: the Butler Chain of Lakes. This network of thirteen interconnected lakes — including Lake Butler, Lake Down, Lake Bessie, and Lake Chase — forms the geographic and cultural heart of one of the most affluent residential communities in Central Florida. Windermere is not a city in the conventional sense; it is a village of roughly 3,000 residents within a broader unincorporated area that includes some of the most expensive real estate in the state.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The Windermere that residents experience is defined by lakefront living, estate architecture, and an intentional commitment to preserving the area's natural character. Properties along the Butler Chain routinely command seven and eight figures, and the community's strict building and development codes have prevented the commercial sprawl that characterizes much of Orange County. Windermere feels more like a private enclave than a suburb — oak-canopied streets, private docks, and lakefront porches that look out over water rather than parking lots. The village has no traffic lights, no chain retail within its boundaries, and a population density that is among the lowest in the region.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Beyond the village proper, the Windermere area includes several of Orlando's most prestigious planned communities. Isleworth, the world-renowned golf and lakefront community, has hosted PGA Tour professionals, professional athletes, and business executives for decades. Keene's Pointe, Butler Bay, and Summerport offer similarly high-end residential options with golf course, lakefront, and gated security features. Windermere Preparatory School, an independent K-12 institution on the Butler Chain, draws families from across the Orlando metro area and abroad who seek a private education with an international curriculum.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What makes Windermere's travel patterns distinct is the combination of extreme proximity to Orlando's global attractions and a population that travels internationally at a rate far above the national average. Windermere is approximately ten minutes from Disney World's Magic Kingdom, fifteen minutes from Universal Orlando, and twenty minutes from the Orange County Convention Center — one of the largest convention facilities in the United States. This proximity means that Windermere residents are not just leisure travelers; they are frequent business travelers who commute to the convention center, International Drive, and the growing Lake Nona Medical City. Many are executives, physicians, attorneys, and entrepreneurs whose careers require regular international travel to London, Frankfurt, São Paulo, and Toronto.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The tourism industry also creates unique passport demand in the Windermere area. While residents themselves travel extensively, the Orlando tourism economy means that Windermere-area families often host international visitors — relatives from Latin America, Europe, and Asia who come to experience Disney and Universal. When those visitors need U.S. visa assistance or when Windermere residents need to travel abroad to visit family, passport urgency follows. Additionally, Disney cast members, Universal team members, and hospitality professionals who live in the Windermere area may need expedited passports for international training, Disney's overseas parks, or cruise line assignments.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Windermere's geographic position also matters for passport logistics. The village itself has no post office — the closest acceptance facilities are in Ocoee, Winter Garden, and the Dr. Phillips area of Orlando. Orlando International Airport (MCO) is approximately twenty-five minutes east via the Florida Turnpike or I-4, making it the primary departure point for international travel. Port Canaveral, the cruise port serving Disney Cruise Line, Royal Caribbean, Carnival, and Norwegian, is approximately fifty minutes east — a manageable drive for Windermere families embarking on Caribbean itineraries. The closest regional passport agency is in Miami, approximately 230 miles south — a drive that is functionally impossible for most Windermere travelers facing an urgent deadline.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Windermere travelers across every demographic: executives with last-minute London board meetings, families departing on Disney cruises from Port Canaveral, physicians traveling to medical conferences in Germany, and students at Windermere Preparatory School heading to semester-abroad programs in Spain. Our courier-based expedited service means you start online from your lakefront estate, your Isleworth golf club, or your Windermere Prep campus — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Orlando</p>
                  <p className="text-white/60 text-xs mt-0.5">200 S Orange Ave, Orlando, FL 32801</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Serving Windermere — approximately 25 miles via Turnpike to downtown Orlando
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Orange County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your Windermere home in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Orlando office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Orange, Osceola, and Seminole Counties — start online, ship overnight, and we handle the rest. No need to drive to a passport agency.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Orlando", href: "/orlando" },
                  { name: "Winter Park", href: "/winter-park" },
                  { name: "Winter Garden", href: "/winter-garden" },
                  { name: "Celebration", href: "/celebration" },
                  { name: "Lake Mary", href: "/lake-mary" },
                  { name: "Tampa", href: "/tampa" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}